import { useState, type FormEvent } from 'react';
import { RefreshCw, AlertTriangle, Package, ArrowDownRight, ArrowUpRight, Plus, Boxes } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useStockHealth, useMouvementsStock, useCreateMouvementStock,
} from '@/hooks/useBetonSprint3';
import { useBetonMatieres } from '@/hooks/useBetonCatalog';
import {
  TYPE_MP_LABELS, TYPE_MVT_STOCK_LABELS,
  type TypeMatierePremiere, type TypeMouvementStock,
} from '@/types';
import { formatNumber, formatFCFA } from '@/utils/currency';
import { extractError } from '@/api/client';

const fmtDT = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const StocksHealthPage = () => {
  const [mpFilter, setMpFilter]   = useState('');
  const [typeFilter, setTypeF]    = useState<TypeMouvementStock | ''>('');
  const [dialogOpen, setDialog]   = useState(false);
  const [actionError, setError]   = useState<string | null>(null);

  const { data: health, isLoading: hLoading, refetch: refetchH } = useStockHealth();
  const { data: mvts, isLoading: mLoading, refetch: refetchM }   = useMouvementsStock({
    matierePremiereId: mpFilter || undefined,
    type: typeFilter || undefined,
    limit: 100,
  });

  const refetch = () => { void refetchH(); void refetchM(); };

  return (
    <>
      <Topbar
        title="Stocks matières premières"
        subtitle="Module 7 — Santé stocks + audit mouvements (entrées achats · sorties auto gâchées/OF · ajustements)"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={refetch}>
              <RefreshCw size={14} className={(hLoading || mLoading) ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialog(true)}>
              <Plus size={14} /> Saisie mouvement
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        {/* Santé stocks */}
        {health && (
          <>
            <div className="ds-kpi-strip">
              <div className="ds-ks ks-blue">
                <div className="ds-ks-label">Matières actives</div>
                <div className="ds-ks-value">{health.total.lignes}</div>
                <div className="ds-ks-sub">Suivi stock</div>
              </div>
              <div className={`ds-ks ${health.total.alertes > 0 ? 'ks-red' : 'ks-green'}`}>
                <div className="ds-ks-label">Alertes</div>
                <div className="ds-ks-value">{health.total.alertes}</div>
                <div className="ds-ks-sub">Sous seuil ou &lt; 7j stock</div>
              </div>
              <div className="ds-ks ks-purple">
                <div className="ds-ks-label">Valorisation totale</div>
                <div className="ds-ks-value" style={{ fontSize: 18 }}>
                  {formatFCFA(health.lignes.reduce((s, l) => s + (l.valorisationCents ?? 0), 0))}
                </div>
                <div className="ds-ks-sub">CMP × stock</div>
              </div>
              <div className="ds-ks ks-orange">
                <div className="ds-ks-label">Mouvements 30j</div>
                <div className="ds-ks-value">
                  {formatNumber(health.lignes.reduce((s, l) => s + l.conso30j, 0))}
                </div>
                <div className="ds-ks-sub">Sorties cumulées</div>
              </div>
            </div>

            <div className="ds-table-panel" style={{ marginBottom: 16 }}>
              <div className="ds-table-header">
                <div>
                  <div className="ds-table-title">Santé stocks par matière</div>
                  <div className="ds-table-sub">Cliquer une ligne pour filtrer les mouvements</div>
                </div>
              </div>
              <table className="ds-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Code</th>
                    <th>Nom</th>
                    <th style={{ width: 100 }}>Type</th>
                    <th className="ds-am" style={{ width: 100 }}>Stock</th>
                    <th className="ds-am" style={{ width: 80 }}>Seuil</th>
                    <th className="ds-am" style={{ width: 90 }}>Conso 30j</th>
                    <th style={{ width: 90 }}>Jours stock</th>
                    <th className="ds-am" style={{ width: 110 }}>Valorisation</th>
                  </tr>
                </thead>
                <tbody>
                  {health.lignes.map((l) => (
                    <tr key={l.id} onClick={() => setMpFilter(l.id === mpFilter ? '' : l.id)}
                        style={{ cursor: 'pointer', background: mpFilter === l.id ? 'rgba(0,127,71,0.06)' : undefined }}>
                      <td><span className="ds-ref-code">{l.code}</span></td>
                      <td>{l.nom}</td>
                      <td style={{ fontSize: 11 }}>
                        {l.type ? TYPE_MP_LABELS[l.type as TypeMatierePremiere] : <span style={{ color: 'var(--tx-3)' }}>(non typé)</span>}
                      </td>
                      <td className="ds-am" style={{ fontWeight: l.sousSeuil ? 600 : 400, color: l.sousSeuil ? 'var(--danger)' : 'inherit' }}>
                        {l.sousSeuil && <AlertTriangle size={10} style={{ display: 'inline', marginRight: 3 }} />}
                        {formatNumber(l.stock)} {l.unite}
                      </td>
                      <td className="ds-am" style={{ fontSize: 11, color: 'var(--tx-3)' }}>{formatNumber(l.seuil)}</td>
                      <td className="ds-am" style={{ fontSize: 11 }}>{formatNumber(l.conso30j)}</td>
                      <td style={{ fontSize: 11, color: l.joursStockRestants != null && l.joursStockRestants < 7 ? 'var(--warning)' : 'inherit', fontWeight: l.joursStockRestants != null && l.joursStockRestants < 7 ? 600 : 400 }}>
                        {l.joursStockRestants != null ? `${l.joursStockRestants}j` : '∞'}
                      </td>
                      <td className="ds-am" style={{ fontSize: 11 }}>
                        {l.valorisationCents != null ? formatFCFA(l.valorisationCents) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Mouvements */}
        <div className="ds-filters">
          {mpFilter && (
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setMpFilter('')}>
              ✕ Filtre MP actif
            </button>
          )}
          <select className="ds-fi" value={typeFilter} onChange={(e) => setTypeF(e.target.value as TypeMouvementStock | '')} style={{ width: 'auto' }}>
            <option value="">Tous types</option>
            {(Object.keys(TYPE_MVT_STOCK_LABELS) as TypeMouvementStock[]).map((t) => (
              <option key={t} value={t}>{TYPE_MVT_STOCK_LABELS[t]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Audit mouvements stock</div>
              <div className="ds-table-sub">{mvts?.data.length ?? 0} mouvements{mpFilter ? ' (filtré)' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Quand</th>
                <th style={{ width: 130 }}>Type</th>
                <th>Matière</th>
                <th className="ds-am" style={{ width: 100 }}>Quantité</th>
                <th className="ds-am" style={{ width: 100 }}>Stock après</th>
                <th>Source · motif</th>
              </tr>
            </thead>
            <tbody>
              {(mvts?.data ?? []).map((m) => {
                const qty = parseFloat(m.quantite);
                return (
                  <tr key={m.id}>
                    <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDT(m.createdAt)}</td>
                    <td>
                      <span className="ds-bd" style={{ background: qty > 0 ? 'var(--success)' : 'var(--info)', color: 'white', fontSize: 10 }}>
                        {qty > 0 ? <ArrowUpRight size={9} /> : <ArrowDownRight size={9} />}
                        {TYPE_MVT_STOCK_LABELS[m.type]}
                      </span>
                    </td>
                    <td style={{ fontSize: 11 }}>
                      <span className="ds-ref-code">{m.matierePremiere?.code}</span>
                      <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{m.matierePremiere?.nom}</div>
                    </td>
                    <td className="ds-am" style={{ color: qty > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {qty > 0 ? '+' : ''}{formatNumber(qty)} {m.matierePremiere?.unite}
                    </td>
                    <td className="ds-am" style={{ fontSize: 11 }}>
                      {m.stockApres != null ? formatNumber(parseFloat(m.stockApres)) : '—'}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{m.motif ?? m.refExterne ?? '—'}</td>
                  </tr>
                );
              })}
              {(mvts?.data ?? []).length === 0 && !mLoading && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>
                  <Boxes size={24} style={{ display: 'inline-block', marginBottom: 4 }} />
                  <div>Aucun mouvement</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <MouvementDialog open={dialogOpen} onClose={() => setDialog(false)} onError={setError} />
    </>
  );
};

// ── Dialog création mouvement manuel ────────────────────

const MouvementDialog = ({ open, onClose, onError }: { open: boolean; onClose: () => void; onError: (msg: string | null) => void }) => {
  const [matiereId, setMatiereId] = useState('');
  const [type, setType]           = useState<TypeMouvementStock>('entree_achat');
  const [qte, setQte]             = useState('');
  const [prix, setPrix]           = useState('');
  const [ref, setRef]             = useState('');
  const [motif, setMotif]         = useState('');
  const [error, setErr]           = useState<string | null>(null);
  const { data: mpsData } = useBetonMatieres({ actif: true, limit: 200 });
  const create = useCreateMouvementStock();
  const mps = mpsData?.data ?? [];

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); onError(null);
    const qNum = parseFloat(qte);
    if (!matiereId || isNaN(qNum)) { setErr('Champs requis'); return; }
    const signed = type === 'entree_achat' ? Math.abs(qNum)
                 : type === 'sortie_manuelle' ? -Math.abs(qNum)
                 : qNum;
    try {
      await create.mutateAsync({
        matierePremiereId: matiereId,
        type,
        quantite: signed,
        prixUnitaireCents: prix ? parseInt(prix, 10) : undefined,
        refExterne: ref.trim() || undefined,
        motif: motif.trim() || undefined,
      });
      setMatiereId(''); setQte(''); setPrix(''); setRef(''); setMotif('');
      onClose();
    } catch (err) { const m = extractError(err).message; setErr(m); onError(m); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Saisie mouvement stock" subtitle="Entrée achat (recalcule CMP) · Sortie manuelle · Inventaire" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Matière première *">
          <select value={matiereId} onChange={(e) => setMatiereId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
            <option value="">— Sélectionner —</option>
            {mps.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.nom}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type *">
            <select value={type} onChange={(e) => setType(e.target.value as TypeMouvementStock)} className="ds-fi" style={{ width: '100%' }}>
              <option value="entree_achat">Entrée achat (réception)</option>
              <option value="sortie_manuelle">Sortie manuelle</option>
              <option value="inventaire">Inventaire (correction signée)</option>
            </select>
          </Field>
          <Field label="Quantité *">
            <input type="number" step="0.001" value={qte} onChange={(e) => setQte(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder={type === 'inventaire' ? '+10 ou -5' : '50'} />
          </Field>
        </div>

        {type === 'entree_achat' && (
          <Field label="Prix unitaire achat (cents FCFA) — recalcule CMP">
            <input type="number" min="0" value={prix} onChange={(e) => setPrix(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="ex: 8500" />
          </Field>
        )}

        <Field label={type === 'entree_achat' ? 'N° Bon de Réception fournisseur' : 'Référence externe'}>
          <input value={ref} onChange={(e) => setRef(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder={type === 'entree_achat' ? 'BR-FOURN-001' : ''} />
        </Field>

        <Field label="Motif">
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!matiereId || !qte || create.isPending}>
            <Package size={14} /> {create.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
